import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: MapPin,
    title: 'Address',
    details: ['PO BOX 14076', 'Saint Petersburg, FL 33733'],
  },
  {
    icon: Phone,
    title: 'Phone',
    details: ['877.495.2634'],
    link: 'tel:+18774952634',
  },
  {
    icon: Mail,
    title: 'Email',
    details: ['info@lane4foundation.com'],
    link: 'mailto:info@lane4foundation.com',
  },
  {
    icon: Clock,
    title: 'Office Hours',
    details: ['Monday - Friday', '9:00 AM - 5:00 PM EST'],
  },
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  // Spam defences: the honeypot is invisible to people, and the timestamp lets the
  // server reject submissions that arrive faster than anyone could type.
  const [honeypot, setHoneypot] = useState('');
  const formLoadedAt = useRef(Date.now());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        website: honeypot,
        form_loaded_at: formLoadedAt.current,
      });
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
      // The server returns 429 with a human-readable reason when someone has sent
      // several messages in a short time; show it instead of a generic failure.
      if (error?.response?.status === 429 && error.response.data?.detail) {
        setErrorMessage(error.response.data.detail);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Mail className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold">
              Contact Us
            </h1>
            <p className="mt-6 text-xl text-slate-300 max-w-2xl mx-auto">
              Have questions about our programs or want to get involved? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-8">Get in Touch</h2>
              <div className="space-y-8">
                {contactInfo.map((item, index) => (
                  <div key={item.title} className="flex items-start space-x-4" data-testid={`contact-info-${index}`}>
                    <div className="w-12 h-12 bg-red-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{item.title}</h3>
                      {item.details.map((detail, i) => (
                        item.link ? (
                          <a
                            key={i}
                            href={item.link}
                            className="block text-slate-600 hover:text-red-600 transition-colors"
                          >
                            {detail}
                          </a>
                        ) : (
                          <p key={i} className="text-slate-600">{detail}</p>
                        )
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div className="mt-12 pt-8 border-t border-slate-200">
                <h3 className="font-bold text-slate-900 mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  {['Facebook', 'Twitter', 'Instagram', 'LinkedIn'].map((social) => (
                    <a
                      key={social}
                      href="#"
                      className="w-10 h-10 bg-slate-100 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors text-slate-600"
                      aria-label={social}
                    >
                      <span className="text-xs font-bold">{social[0]}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {submitStatus === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-slate-50 p-12 border border-slate-200 text-center"
                  data-testid="contact-success"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-slate-900">Message Sent!</h3>
                  <p className="mt-4 text-slate-600">
                    Thank you for reaching out. We'll get back to you as soon as possible, typically within 1-2 business days.
                  </p>
                  <Button
                    onClick={() => setSubmitStatus(null)}
                    className="mt-6 bg-slate-900 text-white hover:bg-red-600 rounded-none uppercase tracking-widest font-bold px-8 py-4"
                  >
                    Send Another Message
                  </Button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-slate-50 p-8 border border-slate-200" data-testid="contact-form">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">Send Us a Message</h2>

                  {/*
                    Honeypot: bots fill every field they find, people never see this one.
                    Positioned offscreen rather than display:none, because some bots
                    deliberately skip hidden fields.
                  */}
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-9999px' }}
                    data-testid="contact-honeypot"
                  />

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Full Name *</Label>
                      <Input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="mt-1 border-slate-200 bg-white"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Email *</Label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="mt-1 border-slate-200 bg-white"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Phone</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="mt-1 border-slate-200 bg-white"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Subject *</Label>
                      <Select
                        value={formData.subject}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}
                        required
                      >
                        <SelectTrigger className="mt-1 border-slate-200 bg-white" data-testid="contact-subject-select">
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General Inquiry</SelectItem>
                          <SelectItem value="application">Program Application</SelectItem>
                          <SelectItem value="donation">Donation Question</SelectItem>
                          <SelectItem value="volunteer">Volunteering</SelectItem>
                          <SelectItem value="sponsorship">Corporate Sponsorship</SelectItem>
                          <SelectItem value="media">Media/Press</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <Label className="text-sm font-medium text-slate-700">Message *</Label>
                    <Textarea
                      required
                      rows={6}
                      placeholder="How can we help you?"
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="mt-1 border-slate-200 bg-white"
                      data-testid="contact-message-input"
                    />
                  </div>

                  {submitStatus === 'error' && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="contact-error">
                      {errorMessage || 'Something went wrong. Please try again or email us directly at info@lane4foundation.org'}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 text-white hover:bg-slate-900 rounded-none uppercase tracking-widest font-bold py-4"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">FAQ</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-6">
            {[
              {
                q: 'How do I apply for assistance?',
                a: 'You can apply by contacting us through this form and selecting "Program Application" as the subject. Our team will send you the necessary forms and guide you through the process.',
              },
              {
                q: 'How long does the application process take?',
                a: 'We typically review applications within 2-3 weeks. For urgent needs, please indicate this in your message and we\'ll do our best to expedite the process.',
              },
              {
                q: 'Can I donate equipment instead of money?',
                a: 'Yes! We accept gently used athletic equipment. Contact us to learn about drop-off locations and what types of equipment we currently need.',
              },
              {
                q: 'Are donations tax-deductible?',
                a: 'Lane 4 For All Kids Foundation is currently in the process of obtaining 501(c)(3) tax-exempt status. Donations may not yet be tax-deductible.',
              },
            ].map((faq, index) => (
              <div key={index} className="bg-white p-6 border border-slate-200" data-testid={`faq-${index}`}>
                <h3 className="font-heading text-lg font-bold text-slate-900">{faq.q}</h3>
                <p className="mt-2 text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
